import "@tento-ui/components/calendar/calendar-column";
import "@tento-ui/components/calendar/calendar-column.mock";
import "@tento-ui/components/round";

import { LonaWebComponent, html } from "@tento-ui/component";
import { Div } from "@tento-ui/component-builtin";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { Divider } from "@tento-ui/components/divider";
import { List } from "@tento-ui/components/list/list";
import { $_maybe, $qs_maybe } from "@tento-ui/dom-selectors";
import { DarkMode } from "@tento-ui/dark-mode";
import { LazySync } from "@tento-ui/lazy";
import { Tracing } from "@tento-core/tracing";
import { LonaModalWebComponent } from "@tento-ui/ui/modal-component";
import { Round } from "@tento-ui/components/round";
import { LonaModalHeader } from "@tento-ui/ui/modal-header-styles";
import { EventCategories, ThemesClient } from "@tento-lona";
import { Actor } from "@tento-lona/sheets";
import { MockCalendarRow } from "@tento-ui/components/calendar/calendar-column.mock";
import { CalendarColumnCellContent } from "@tento-ui/components/calendar/calendar-column-cell-content";
import { DateTime, Hms, Ms, TimePoint } from "@tento-chrono";
import { LonaUserLocal } from "./lona-user-local";
import { reportWriteError } from "./app/errors";

function eventTime(
  start: Hms.Parts.Opt,
  durationMins: number,
): TimePoint.Range {
  const st = TimePoint.ms(Ms.resolve(start));
  const et = st.add({ mins: durationMins });
  return new TimePoint.Range(st, et);
}

function eventTimeLabel(
  start: Hms.Parts.Opt,
  durationMins: number,
): DateTime.Range {
  const st = TimePoint.ms(Ms.resolve(start));
  return new DateTime.Range(
    LonaUserLocal.now().withTime(st.time),
    LonaUserLocal.now().withTime(st.time).add({ mins: durationMins }),
  );
}

@component({ name: "lona-theme-picker-modal" })
export class ThemePickerModal extends LonaModalWebComponent {
  static instance = new LazySync(() => ThemePickerModal.make());
  static reveal(themes: ThemesClient) {
    ThemePickerModal.instance.get().init(themes).reveal();
  }

  static toggle(themes: ThemesClient) {
    ThemePickerModal.instance.get().init(themes).toggle();
  }

  private _$bg: Round = this.hydrate<Round>("bg")
    .bind(Round.Props.MODAL);
  private _$content = this.hydrate<ThemePickerContent>("content");

  private init(themes: ThemesClient): this {
    this._$content.init(themes);
    return this;
  }

  static $styles: Style.Compat[] = [
    LonaModalWebComponent.$style,
    css`
      :host {
        --std-modal-max-width: 540px;
      }

      #root {
        position: relative;
        padding: 0px;
      }

      #content {
        height: 100%;
      }
    `,
  ];
  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <std-round floating id=bg></std-round>
      <lona-theme-picker-content id="content"></lona-theme-picker-content>
    </std-col>
  `;
}

@component({
  name: "lona-theme-picker-content",
})
export class ThemePickerContent extends LonaWebComponent {
  $fakeCalendar: MockCalendarRow = this.hydrate("fake-calendar");
  themes: Option<ThemesClient> = null;
  private $themeList: Option<HTMLElement> = null;

  init(themes: ThemesClient) {
    this.themes = themes;
    this.bind();
  }

  private selectTheme(name: Option<string>): void {
    if (!name) return;
    $qs_maybe("[selected]", this)?.toggleAttribute("selected", false);
    $_maybe("theme_" + name, this)?.toggleAttribute("selected", true);
  }

  /**
   * The sample events of a theme preview, coloured in `theme` and the page's
   * dark mode — a category table of the preview's own (the theme shown need
   * not be the client's current one).
   */
  static makeEvents(
    theme: Option<ThemesClient.Theme>,
  ): (CalendarColumnCellContent.Props & {
    id: string;
    background: string;
  })[] {
    const categories = new EventCategories({
      theme: () => theme ?? null,
      isDark: () => DarkMode.isDarkMode(),
      platform: "other",
    });
    return [
      {
        id: "call",
        label: "Call",
        time: eventTime({ hrs: 0, mins: 0 }, 90),
        timeLabel: eventTimeLabel({ hrs: 9, mins: 0 }, 90),
        background: categories.item("work").colorOpt() ?? "var(--gray)",
      },
      {
        id: "lunch",
        label: "Lunch",
        time: eventTime({ hrs: 1 }, 60),
        timeLabel: eventTimeLabel({ hrs: 10, mins: 0 }, 60),
        background: categories.item("food").colorOpt() ?? "var(--gray)",
      },
      {
        id: "design-sync",
        label: "Exercise",
        time: eventTime({ hrs: 2 }, 60),
        timeLabel: eventTimeLabel({ hrs: 11, mins: 0 }, 60),
        background: categories.item("exercise").colorOpt() ?? "var(--gray)",
      },
      {
        id: "Commute",
        label: "Focus Time",
        time: eventTime({ hrs: 0 }, 120),
        timeLabel: eventTimeLabel({ hrs: 9, mins: 0 }, 120),
        background: categories.item("transportation").colorOpt() ?? "var(--gray)",
      },
    ];
  }

  bind() {
    const themes = this.themes;
    if (!themes) return;
    this.$themeList?.remove();

    const themeList = List.builder();
    for (const info of themes.builtin.all()) {
      const $icon = Div.make();
      $icon.assignStyles({
        width: "24px",
        height: "24px",
        background: info.preview,
        borderRadius: "8px",
      });
      themeList.row(info.label, {
        $icon,
        id: "theme_" + info.id,
        onHover: async () => {
          this.rebindCalendar(await themes.get(info.id));
        },
        pointerEvents: {
          onClick: async () => {
            const theme = await themes.get(info.id);
            if (!theme) return;
            void themes.setDurable(theme, Actor.EXTERNAL).settled.catch((e: unknown) => reportWriteError("change the theme", e));
            this.selectTheme(theme.name);
          },
        },
        contentMargin: "8px",
      });
    }

    const $themeList = themeList.build();
    $themeList.slot = "themes";
    this.$themeList = $themeList;
    this.appendChild($themeList);
    const currentTheme = themes.live.get();
    this.rebindCalendar(currentTheme);
    this.selectTheme(currentTheme?.name ?? null);
  }

  private rebindCalendar(theme: Option<ThemesClient.Theme>) {
    this.$fakeCalendar.bind(ThemePickerContent.makeEvents(theme));
    this.$fakeCalendar.assignStyles({
      "--sheet-background-color": theme?.semanticColor(
        "sheet",
        "background",
        DarkMode.isDarkMode() ? "dark" : "light",
      ),
    });
  }

  static $styles: Style.Compat[] = [
    LonaModalHeader.$style,
    Divider.$hr,
    css`
      :host {
        height: 100%;
        color: var(--text-sec);
      }

      #root {
        height: 100%;
      }

      #themes {
        min-width: 200px;
        padding: 12px 8px;
      }

      #preview {
        border-radius: 8px;
        flex-grow: 1;
        background: var(--bg-surface-light);
        pointer-events: none;
        overflow: hidden;
        align-items: center;
        margin: 12px;
        border: 1px solid var(--divider);
      }

      #anchor {
        position: relative;
        height: 20px;
        width: 180px;
        margin-top: 25%;
      }

      #fake-calendar {
        position: absolute;
        top: 0px;
        width: 100%;
        box-shadow: var(--box-shadow);
        border-radius: 8px;
        height: calc(48px * 24);
        padding-top: 12px;
      }

      hr {
        margin: 0;
      }
    `,
  ];
  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <std-col id="header" class="lona-modal-header">
        <h1>Calendar Theme</h1>
        <p>Change the colors of your calendar events.</p>
      </std-col>
      <hr />
      <std-row style="flex-grow:1;">
        <std-col id="themes">
          <slot name="themes"></slot>
        </std-col>
        <std-col id="preview">
          <div id="anchor">
            <std-mock-calendar-row
              id="fake-calendar"
            ></std-mock-calendar-row>
          </div>
        </std-col>
      </std-row>
    </std-col>
  `;
}
